import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { clearAuthCache } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      console.warn("Auth check failed", e?.response?.status || e?.message);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear, non-silent handling when the session truly expired (refresh failed).
  useEffect(() => {
    const onExpired = () => {
      clearAuthCache();
      setUser((u) => {
        if (u) toast.error("Ihre Sitzung ist abgelaufen – bitte erneut anmelden.");
        return false;
      });
    };
    window.addEventListener("session-expired", onExpired);
    return () => window.removeEventListener("session-expired", onExpired);
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed", e?.response?.status || e?.message);
    }
    await clearAuthCache();
    try { sessionStorage.removeItem("accountsChosen"); } catch (_) {}
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout }),
    [user, loading, checkAuth, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
